import { useEffect } from "react";

const SCRIPT_TAG_ID_PREFIX = "css-jsonld-";

/**
 * Inject one or more JSON-LD schema blocks into <head>.
 * Each schema becomes its own <script type="application/ld+json"> tag,
 * and is removed on unmount so per-page schemas don't leak across routes.
 *
 * Usage:
 *   useJsonLd([organizationSchema, breadcrumbSchema]);
 *
 * Schema docs: https://schema.org/ + https://search.google.com/test/rich-results
 */
export function useJsonLd(schemas) {
  useEffect(() => {
    if (!schemas) return;
    const list = Array.isArray(schemas) ? schemas : [schemas];
    const tags = [];

    list.forEach((schema, idx) => {
      if (!schema) return;
      const id = `${SCRIPT_TAG_ID_PREFIX}${idx}-${Math.random().toString(36).slice(2, 8)}`;
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      try {
        el.text = JSON.stringify(schema);
      } catch {
        return;
      }
      document.head.appendChild(el);
      tags.push(el);
    });

    return () => {
      tags.forEach((t) => t.parentNode && t.parentNode.removeChild(t));
    };
  }, [JSON.stringify(schemas)]);
}

// -------- Reusable building blocks --------
export const SITE = {
  url: "https://creativesalessource.com",
  name: "Creative Sales Source LLC",
  legalName: "Creative Sales Source LLC",
  description:
    "Outsourced sales representation for building-materials manufacturers across the Midwest. Connecting your products to distributors and dealers since 2012.",
  logo: "https://creativesalessource.com/uploads/lumber-semi.png",
  email: "info@creativesalessource.com",
  phone: "+1-847-354-0754",
  founded: "2012",
  city: "Chicago",
  region: "IL",
  country: "US",
  // Approximate geo for Chicago, IL — replace with your exact office coords if desired
  geo: { lat: 41.8781, lng: -87.6298 },
  social: {
    linkedin: "https://www.linkedin.com/company/creative-sales-source/",
    facebook: "https://www.facebook.com/salessourcess/",
    instagram: "https://www.instagram.com/creativesalessource/",
    x: "https://www.x.com/salessourcellc",
  },
  areaServed: [
    "Indiana",
    "Illinois",
    "Wisconsin",
    "Minnesota",
    "Iowa",
    "Nebraska",
    "North Dakota",
    "South Dakota",
    "Montana",
  ],
};

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE.url}/#organization`,
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE.url,
    logo: {
      "@type": "ImageObject",
      url: SITE.logo,
    },
    description: SITE.description,
    foundingDate: SITE.founded,
    email: SITE.email,
    telephone: SITE.phone,
    address: {
      "@type": "PostalAddress",
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      addressCountry: SITE.country,
    },
    sameAs: [
      SITE.social.linkedin,
      SITE.social.facebook,
      SITE.social.instagram,
      SITE.social.x,
    ],
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE.url}/#localbusiness`,
    name: SITE.name,
    image: SITE.logo,
    url: SITE.url,
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$$",
    description: SITE.description,
    address: {
      "@type": "PostalAddress",
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      addressCountry: SITE.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: SITE.geo.lat,
      longitude: SITE.geo.lng,
    },
    areaServed: SITE.areaServed.map((s) => ({
      "@type": "State",
      name: s,
    })),
    sameAs: [
      SITE.social.linkedin,
      SITE.social.facebook,
      SITE.social.instagram,
      SITE.social.x,
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "17:00",
      },
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.url}/#website`,
    name: SITE.name,
    url: SITE.url,
    publisher: { "@id": `${SITE.url}/#organization` },
  };
}

export function breadcrumbSchema(trail) {
  // trail = [{ name: "Home", path: "/" }, ...]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: `${SITE.url}${t.path}`,
    })),
  };
}

export function articleSchema(post) {
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${SITE.url}/insights/${post.slug}#article`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE.url}/insights/${post.slug}`,
    },
    headline: post.title,
    description: post.excerpt || undefined,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    articleSection: post.category || "Industry Insights",
    author: {
      "@type": "Organization",
      name: SITE.name,
      url: SITE.url,
    },
    publisher: { "@id": `${SITE.url}/#organization` },
  };
}

export function serviceSchema(services) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Outsourced Sales Representation",
    provider: { "@id": `${SITE.url}/#organization` },
    areaServed: SITE.areaServed.map((s) => ({
      "@type": "State",
      name: s,
    })),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Manufacturer Representation Services",
      itemListElement: (services || []).map((s) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: s.title,
          description: s.body,
        },
      })),
    },
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (items || []).map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}

export default useJsonLd;
